import { useEffect, useState, type FormEvent } from 'react';
import { db, withSyncMeta } from '@/offline/db';
import { enqueueSync } from '@/offline/sync';
import { useAuthStore } from '@/lib/auth-store';
import { minScopeForDrug, scopeBlockReason } from '@/lib/scope';
import { useProtocolStore } from '@/modules/protocol/protocol-store';
import { allergyContraindications, exceedsMaxDose } from '@/lib/drug-safety';
import type { EPCR, MedicationGiven } from '@/types';
import { cn } from '@/lib/cn';
import { Plus, Pill, Clock, AlertTriangle, Shield, BookOpen } from 'lucide-react';
import { DemoFillButton } from '@/components/DemoFillButton';
import { demoMedicationRows } from '@/lib/demo-data';

interface Props {
  epcr: EPCR;
}

// RI EMS full formulary (used in deviation mode only)
const FULL_FORMULARY = [
  { name: 'Epinephrine 1:1,000', controlled: false },
  { name: 'Epinephrine 1:10,000', controlled: false },
  { name: 'Nitroglycerin', controlled: false },
  { name: 'Aspirin', controlled: false },
  { name: 'Albuterol', controlled: false },
  { name: 'Ipratropium', controlled: false },
  { name: 'Naloxone (Narcan)', controlled: false },
  { name: 'Dextrose 10%', controlled: false },
  { name: 'Glucagon', controlled: false },
  { name: 'Amiodarone', controlled: false },
  { name: 'Adenosine', controlled: false },
  { name: 'Atropine', controlled: false },
  { name: 'Diphenhydramine (Benadryl)', controlled: false },
  { name: 'Ondansetron (Zofran)', controlled: false },
  { name: 'Acetaminophen', controlled: false },
  { name: 'Ibuprofen', controlled: false },
  { name: 'Ketorolac (Toradol)', controlled: false },
  { name: 'Magnesium Sulfate', controlled: false },
  { name: 'Calcium Chloride', controlled: false },
  { name: 'Sodium Bicarbonate', controlled: false },
  { name: 'Normal Saline', controlled: false },
  { name: 'Lactated Ringers', controlled: false },
  { name: 'Oral Glucose', controlled: false },
  { name: 'Methylprednisolone', controlled: false },
  { name: 'Hydrocortisone', controlled: false },
  { name: 'Dexamethasone', controlled: false },
  { name: 'Levetiracetam', controlled: false },
  { name: 'Fentanyl', controlled: true },
  { name: 'Morphine', controlled: true },
  { name: 'Ketamine', controlled: true },
  { name: 'Midazolam (Versed)', controlled: true },
  { name: 'Diazepam (Valium)', controlled: true },
  { name: 'Lorazepam (Ativan)', controlled: true },
];

const ROUTES = [
  'IV', 'IO', 'IM', 'IN (Intranasal)', 'SL (Sublingual)',
  'PO (Oral)', 'Nebulized', 'Topical', 'Rectal', 'ET (Endotracheal)',
];

const UNITS = ['mg', 'mcg', 'g', 'mL', 'L', 'mEq', 'units', 'puffs', 'spray'];

export function MedicationsTab({ epcr }: Props) {
  const { user } = useAuthStore();
  const { permittedMedications, activeProtocols, completeStep, addDeviation } = useProtocolStore();
  const [meds, setMeds] = useState<MedicationGiven[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [deviationMode, setDeviationMode] = useState(false);
  const [form, setForm] = useState(defaultForm());

  useEffect(() => {
    loadMeds();
  }, [epcr.id]);

  async function loadMeds() {
    const records = await db.medications_given.where('epcr_id').equals(epcr.id).sortBy('timestamp');
    setMeds(records);
  }

  // Permitted meds from active protocols
  const permitted = permittedMedications();
  const hasActiveProtocols = activeProtocols().length > 0;

  // When user selects a drug, find if it's in permitted list
  const selectedMatch = permitted.find((m) => m.drugName === form.drug_name);
  const fullDrugRecord = FULL_FORMULARY.find((d) => d.name === form.drug_name);
  const isControlled = fullDrugRecord?.controlled ?? false;

  // Auto-fill dose + route + unit when a protocol-backed med is selected
  useEffect(() => {
    if (!selectedMatch) return;
    const { dose } = selectedMatch;
    // Parse adult dose (e.g., "0.4 mg" → dose=0.4, unit=mg)
    const match = dose.adultDose.match(/^([\d.]+)\s*(\w+)/);
    if (match) {
      setForm((f) => ({
        ...f,
        dose: f.dose || match[1],
        unit: f.unit || match[2],
        route: f.route || dose.adultRoute,
      }));
    }
  }, [form.drug_name]);

  // Contraindications based on allergies — Finding 14 (HIGH). Now
  // uses drug-class matching (Penicillin allergy flags Amoxicillin,
  // Sulfa flags Bactrim, etc.) instead of the legacy substring match
  // that missed every cross-class hit. allergyContraindications()
  // falls back to substring for typed allergies outside the class
  // table so unknown allergens still produce a warning.
  const contraindications = form.drug_name && epcr.allergies.length > 0
    ? allergyContraindications(form.drug_name, epcr.allergies)
    : [];

  // Dose-over-max check — Finding 13 (CRITICAL). Parses the
  // protocol's maxDose text into a numeric upper bound; if the
  // entered dose exceeds it the EMT must supply a medical-control
  // override authorization string. Units must match (we don't
  // auto-convert mcg ↔ mg; the unit dropdown should already be
  // constrained by the protocol).
  const doseCheck = exceedsMaxDose(form.dose, form.unit, selectedMatch?.dose.maxDose);
  const doseExceedsMax = doseCheck.exceeds;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!form.drug_name || !user) return;

    const isDeviation = deviationMode || !selectedMatch;

    if (isDeviation && !form.deviation_reason.trim()) {
      alert('Deviation requires a clinical justification.');
      return;
    }

    // Hard scope block — Sprint K. Above-scope drugs cannot be
    // inserted regardless of deviation. RI Protocol §1.01.
    const required = minScopeForDrug(form.drug_name);
    const scope = scopeBlockReason(user.cert_level, required);
    if (scope) {
      alert(`${form.drug_name}: ${scope}`);
      return;
    }

    // Finding 13 — hard block on doses above protocol max unless
    // a medical-control authorization string is supplied.
    if (doseExceedsMax && !form.medical_control_override.trim()) {
      alert(
        `Dose ${form.dose} ${form.unit} exceeds the protocol maximum ` +
        `(${(doseCheck as { max: number; unit: string }).max} ${(doseCheck as { unit: string }).unit}). ` +
        `Enter the medical-control authorization (provider name + order time) to proceed.`,
      );
      return;
    }

    // Finding 14 — when a drug-class allergy is flagged, require the
    // EMT to type an override justification (not a checkbox). Audit
    // trail goes into allergy_override_reason on the row.
    if (contraindications.length > 0 && !form.allergy_override_reason.trim()) {
      alert(
        `${contraindications.length} allergy contraindication(s) flagged for ${form.drug_name}. ` +
        `Document the clinical rationale for administering anyway, or change the medication.`,
      );
      return;
    }

    // Finding 23 — controlled substances require waste witness + waste
    // amount text at save (DEA/CSR chain-of-custody). Witness must be
    // someone other than the administering EMT.
    if (isControlled) {
      if (!form.waste_witness.trim() || !form.waste_amount.trim()) {
        alert(
          `${form.drug_name} is a controlled substance — waste witness and waste amount are both required at save.`,
        );
        return;
      }
      if (form.waste_witness.trim().toLowerCase() === user.full_name.toLowerCase()) {
        alert('Waste witness cannot be the administering EMT. Enter a second crew member.');
        return;
      }
    }

    const med: MedicationGiven = {
      id: crypto.randomUUID(),
      epcr_id: epcr.id,
      drug_name: form.drug_name,
      dose: form.dose,
      unit: form.unit,
      route: form.route,
      timestamp: new Date(form.timestamp).toISOString(),
      performed_by: user.id,
      patient_response: form.patient_response || null,
      controlled_substance: isControlled,
      waste_documented: isControlled,
      waste_witness: isControlled ? form.waste_witness.trim() : null,
      waste_amount: isControlled ? form.waste_amount.trim() : null,
      allergy_override_reason: form.allergy_override_reason.trim() || null,
      protocol_id: selectedMatch?.protocolId ?? null,
      protocol_step_order: selectedMatch?.stepOrder ?? null,
      is_deviation: isDeviation,
      // Append medical-control override to the deviation reason so it
      // survives in the existing column without a schema change. If a
      // bare deviation_reason was provided, prepend.
      deviation_reason: isDeviation
        ? [form.deviation_reason, doseExceedsMax && form.medical_control_override
            ? `[OVER MAX: ${form.medical_control_override}]` : '']
            .filter(Boolean).join(' — ')
        : (doseExceedsMax && form.medical_control_override
            ? `OVER MAX (medical control): ${form.medical_control_override}`
            : null),
      created_at: new Date().toISOString(),
    };

    await db.medications_given.add(withSyncMeta(med));
    await enqueueSync('medications_given', med.id, 'insert', med as unknown as Record<string, unknown>);

    // Mark protocol step complete if this was a protocol med
    if (selectedMatch) {
      await completeStep({
        protocolId: selectedMatch.protocolId,
        stepOrder: selectedMatch.stepOrder,
        linkedMedicationId: med.id,
      });
    }

    if (isDeviation) {
      await addDeviation({
        protocolId: selectedMatch?.protocolId ?? activeProtocols()[0]?.id ?? 'unknown',
        stepOrder: selectedMatch?.stepOrder ?? null,
        reason: form.deviation_reason,
        actionType: 'medication',
        actionId: med.id,
      });
    }

    setForm(defaultForm());
    setDeviationMode(false);
    setShowForm(false);
    loadMeds();
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h3 className="text-lg font-semibold text-slate-200">
          Medications ({meds.length})
        </h3>
        <div className="flex items-center gap-2">
          <DemoFillButton
            label="Fill meds demo"
            onFill={async () => {
              if (!user) return;
              const rows = demoMedicationRows(epcr.id, user.id);
              for (const row of rows) {
                await db.medications_given.add(withSyncMeta(row));
                await enqueueSync('medications_given', row.id, 'insert', row as unknown as Record<string, unknown>);
              }
              await loadMeds();
            }}
          />
          {!epcr.locked && (
            <button onClick={() => setShowForm(!showForm)} className="btn-primary gap-1">
              <Plus size={16} /> Add Medication
            </button>
          )}
        </div>
      </div>

      {hasActiveProtocols && !showForm && (
        <div className="flex items-center gap-2 rounded-lg border border-brand-500/30 bg-brand-500/5 px-3 py-2 text-xs text-brand-300">
          <BookOpen size={14} />
          {permitted.length} medication{permitted.length !== 1 ? 's' : ''} available from active protocol{activeProtocols().length !== 1 ? 's' : ''}
        </div>
      )}

      {showForm && (
        <form onSubmit={handleSubmit} className="rounded-lg border border-brand-500/30 bg-slate-800 p-4 space-y-4">
          {/* Deviation toggle */}
          {hasActiveProtocols && (
            <div className="flex items-center gap-3 rounded-lg border border-slate-700 bg-slate-900 p-3">
              <Shield size={16} className={deviationMode ? 'text-ems-amber' : 'text-brand-400'} />
              <div className="flex-1 text-xs">
                <div className="font-medium text-slate-200">
                  {deviationMode ? 'Deviation Mode' : 'Protocol Mode'}
                </div>
                <div className="text-slate-400">
                  {deviationMode
                    ? 'Any medication. Clinical justification required.'
                    : `Only protocol-permitted medications (${permitted.length}) with recommended doses.`}
                </div>
              </div>
              <button
                type="button"
                onClick={() => {
                  setDeviationMode(!deviationMode);
                  setForm((f) => ({ ...f, drug_name: '', dose: '', unit: '', route: '' }));
                }}
                className={cn(
                  'rounded-md border px-3 py-1.5 text-xs font-medium',
                  deviationMode ? 'border-ems-amber text-ems-amber' : 'border-slate-600 text-slate-400',
                )}
              >
                {deviationMode ? 'Back to Protocol' : 'Deviate'}
              </button>
            </div>
          )}

          {/* Drug selector */}
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-300">Medication</label>
            <select
              value={form.drug_name}
              onChange={(e) => setForm((f) => ({ ...f, drug_name: e.target.value }))}
              className="input-field"
              required
            >
              <option value="">Select medication...</option>
              {!hasActiveProtocols || deviationMode ? (
                FULL_FORMULARY.map((d) => (
                  <option key={d.name} value={d.name}>
                    {d.name}{d.controlled ? ' (CS)' : ''}
                  </option>
                ))
              ) : (
                permitted.map((m) => (
                  <option key={`${m.protocolId}-${m.stepOrder}`} value={m.drugName}>
                    {m.drugName}
                  </option>
                ))
              )}
            </select>
            {selectedMatch && (
              <div className="mt-2 rounded bg-slate-900 p-2 text-xs text-brand-300">
                <div className="flex items-center gap-1">
                  <BookOpen size={12} />
                  <span>Protocol dose: <strong>{selectedMatch.dose.adultDose} {selectedMatch.dose.adultRoute}</strong></span>
                </div>
                {selectedMatch.dose.pediatricDose && (
                  <div className="ml-4 text-slate-400">Pedi: {selectedMatch.dose.pediatricDose} {selectedMatch.dose.pediatricRoute}</div>
                )}
                {selectedMatch.dose.maxDose && (
                  <div className="ml-4 text-slate-400">Max: {selectedMatch.dose.maxDose}</div>
                )}
                {selectedMatch.dose.repeatInterval && (
                  <div className="ml-4 text-slate-400">Repeat: q{selectedMatch.dose.repeatInterval} × {selectedMatch.dose.maxRepeats ?? '∞'}</div>
                )}
              </div>
            )}
          </div>

          {/* Contraindication alert */}
          {contraindications.length > 0 && (
            <div className="flex items-start gap-2 rounded-lg border border-red-700 bg-red-950 p-3 text-sm text-red-300">
              <AlertTriangle size={18} className="mt-0.5" />
              <div>
                <strong>CONTRAINDICATION ALERT</strong>
                {contraindications.map((c, i) => <div key={i}>{c}</div>)}
              </div>
            </div>
          )}

          {/* Controlled substance warning + waste documentation gate.
              Finding 23: witness + amount are now hard-required at
              save; without them the form blocks. */}
          {isControlled && (
            <div className="rounded-lg border border-amber-700 bg-amber-950 p-3 text-sm space-y-3">
              <div className="flex items-center gap-2 text-amber-300">
                <Shield size={18} />
                <strong>Controlled substance — waste documentation required at save</strong>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <label className="mb-1 block text-xs font-medium text-amber-200">Waste witness (crew member) *</label>
                  <input
                    type="text"
                    value={form.waste_witness}
                    onChange={(e) => setForm((f) => ({ ...f, waste_witness: e.target.value }))}
                    className="input-field"
                    placeholder="Full name of second crew member"
                    required
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-amber-200">Waste amount *</label>
                  <input
                    type="text"
                    value={form.waste_amount}
                    onChange={(e) => setForm((f) => ({ ...f, waste_amount: e.target.value }))}
                    className="input-field"
                    placeholder='e.g., "75 mcg of 100 mcg drawn, wasted"'
                    required
                  />
                </div>
              </div>
            </div>
          )}

          {/* Allergy override (Finding 14): when contraindications fire,
              the EMT must type a justification — checkbox-only overrides
              were the previous failure mode. */}
          {contraindications.length > 0 && (
            <div>
              <label className="mb-1 block text-sm font-medium text-ems-red">
                Allergy override justification (required) *
              </label>
              <textarea
                value={form.allergy_override_reason}
                onChange={(e) => setForm((f) => ({ ...f, allergy_override_reason: e.target.value }))}
                className="input-field min-h-[3rem] resize-y"
                placeholder="e.g., Mild rash documented decades ago; benefit > risk; physician contacted on-line."
                required
              />
            </div>
          )}

          {/* Medical-control override (Finding 13): unlocks over-max
              dose entry. Without an authorization string the submit
              handler refuses. */}
          {doseExceedsMax && (
            <div>
              <label className="mb-1 block text-sm font-medium text-ems-red">
                Medical-control authorization (required for over-max dose) *
              </label>
              <input
                type="text"
                value={form.medical_control_override}
                onChange={(e) => setForm((f) => ({ ...f, medical_control_override: e.target.value }))}
                className="input-field"
                placeholder='e.g., "Dr. Smith @ Rhode Island Hospital, 14:32, order via radio"'
                required
              />
            </div>
          )}

          <div className="grid gap-4 sm:grid-cols-3">
            <div>
              <label className={cn(
                'mb-1 block text-sm font-medium',
                doseExceedsMax ? 'text-ems-red' : 'text-slate-300',
              )}>
                Dose {doseExceedsMax && '⚠ OVER MAX'}
              </label>
              {/* Finding 13: number input with stepped decimals replaces
                  free-text. A misplaced decimal becomes a parse error
                  the browser visualizes, not a silent 10× overdose. */}
              <input
                type="number"
                inputMode="decimal"
                step="0.01"
                min="0"
                value={form.dose}
                onChange={(e) => setForm((f) => ({ ...f, dose: e.target.value }))}
                className={cn(
                  'input-field',
                  doseExceedsMax && 'border-ems-red focus:border-ems-red',
                )}
                placeholder="Amount"
                required
              />
              {doseExceedsMax && (
                <p className="mt-1 text-[11px] text-ems-red">
                  Exceeds protocol max ({(doseCheck as { max: number; unit: string }).max} {(doseCheck as { unit: string }).unit}).
                  Medical-control authorization required below.
                </p>
              )}
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-300">Unit</label>
              <select
                value={form.unit}
                onChange={(e) => setForm((f) => ({ ...f, unit: e.target.value }))}
                className="input-field"
                required
              >
                <option value="">Select...</option>
                {UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-300">Route</label>
              <select
                value={form.route}
                onChange={(e) => setForm((f) => ({ ...f, route: e.target.value }))}
                className="input-field"
                required
              >
                <option value="">Select...</option>
                {ROUTES.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-300">Time Administered</label>
              <div className="flex gap-1">
                <input
                  type="datetime-local"
                  value={form.timestamp}
                  onChange={(e) => setForm((f) => ({ ...f, timestamp: e.target.value }))}
                  className="input-field flex-1"
                />
                <button
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, timestamp: new Date().toISOString().slice(0, 16) }))}
                  className="px-2 border border-slate-600 bg-slate-800/80 text-[10px] font-bold uppercase tracking-wider text-slate-300 hover:bg-slate-700"
                  title="Set to current time"
                >
                  Now
                </button>
              </div>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-300">Patient Response</label>
              <select
                value={form.patient_response}
                onChange={(e) => setForm((f) => ({ ...f, patient_response: e.target.value }))}
                className="input-field"
              >
                <option value="">Select...</option>
                <option value="Improved">Improved</option>
                <option value="Unchanged">Unchanged</option>
                <option value="Worse">Worse</option>
              </select>
            </div>
          </div>

          {/* Deviation reason */}
          {(deviationMode || (!selectedMatch && form.drug_name && hasActiveProtocols)) && (
            <div>
              <label className="mb-1 block text-sm font-medium text-ems-amber">
                Clinical Justification for Deviation (required)
              </label>
              <textarea
                value={form.deviation_reason}
                onChange={(e) => setForm((f) => ({ ...f, deviation_reason: e.target.value }))}
                className="input-field min-h-[4rem] resize-y"
                placeholder="e.g., Protocol specified Midazolam but Ketamine given for better hemodynamic stability"
                required
              />
            </div>
          )}

          <div className="flex gap-2">
            <button type="submit" className="btn-primary">Save Medication</button>
            <button type="button" onClick={() => { setShowForm(false); setDeviationMode(false); }} className="btn-secondary">
              Cancel
            </button>
          </div>
        </form>
      )}

      {meds.length > 0 ? (
        <div className="space-y-2">
          {meds.map((med) => (
            <div key={med.id} className="flex items-center gap-3 rounded-lg bg-slate-800 px-4 py-3">
              <div className={cn(
                'flex h-8 w-8 items-center justify-center rounded-full',
                med.controlled_substance ? 'bg-red-900 text-red-400' : 'bg-slate-700 text-slate-400'
              )}>
                <Pill size={16} />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-slate-200">{med.drug_name}</span>
                  {med.controlled_substance && (
                    <span className="rounded bg-red-900 px-1.5 py-0.5 text-xs text-red-300">CS</span>
                  )}
                  {med.is_deviation && (
                    <span className="flex items-center gap-1 rounded bg-amber-900 px-1.5 py-0.5 text-xs text-amber-300">
                      <AlertTriangle size={10} /> DEVIATION
                    </span>
                  )}
                  {med.protocol_id && !med.is_deviation && (
                    <span className="flex items-center gap-1 rounded bg-brand-500/20 px-1.5 py-0.5 text-xs text-brand-300">
                      <BookOpen size={10} /> Step {med.protocol_step_order}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2 text-xs text-slate-400 flex-wrap">
                  <Clock size={12} />
                  {new Date(med.timestamp).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                  <span>| {med.dose} {med.unit} {med.route}</span>
                  {med.patient_response && <span>| Response: {med.patient_response}</span>}
                </div>
                {med.is_deviation && med.deviation_reason && (
                  <p className="mt-1 text-xs italic text-amber-400">
                    Rationale: {med.deviation_reason}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="rounded-lg border border-slate-700 bg-slate-900 py-8 text-center">
          <Pill size={32} className="mx-auto mb-2 text-slate-600" />
          <p className="text-slate-500">No medications administered</p>
        </div>
      )}
    </div>
  );
}

function defaultForm() {
  return {
    drug_name: '',
    dose: '',
    unit: '',
    route: '',
    timestamp: new Date().toISOString().slice(0, 16),
    patient_response: '',
    deviation_reason: '',
    /** Finding 13: medical-control override required when dose exceeds protocol max. */
    medical_control_override: '',
    /** Finding 14: free-text justification when administering despite allergy. */
    allergy_override_reason: '',
    /** Finding 23: CS waste documentation. */
    waste_witness: '',
    waste_amount: '',
  };
}
